/**
 * FHIR Array Builders
 *
 * Utility helpers for managing FHIR resource arrays (identifiers, telecom, addresses). Provides consistent immutable
 * array operations across services.
 */

/**
 * Add a single identifier to a FHIR resource
 *
 * @param resource - Resource with optional identifier array
 * @param identifier - Identifier to add
 * @returns Resource with identifier added
 */
export function addIdentifier<T extends { identifier?: any[] }>(resource: T, identifier: any): T {
  return {
    ...resource,
    identifier: [...(resource.identifier ?? []), identifier],
  };
}

/**
 * Add multiple identifiers to a FHIR resource
 *
 * @param resource - Resource with optional identifier array
 * @param identifiers - Identifiers to add
 * @returns Resource with identifiers added
 */
export function addIdentifiers<T extends { identifier?: any[] }>(resource: T, ...identifiers: any[]): T {
  return {
    ...resource,
    identifier: [...(resource.identifier ?? []), ...identifiers],
  };
}

/**
 * Add telecom (contact) entries to a FHIR resource
 *
 * @param resource - Resource with optional telecom array
 * @param contacts - Contact entries to add
 * @returns Resource with telecom entries added
 */
export function addTelecom<T extends { telecom?: any[] }>(resource: T, ...contacts: any[]): T {
  return {
    ...resource,
    telecom: [...(resource.telecom ?? []), ...contacts],
  };
}

/**
 * Add address entries to a FHIR resource
 *
 * @param resource - Resource with optional address array
 * @param addresses - Address entries to add
 * @returns Resource with addresses added
 */
export function addAddress<T extends { address?: any[] }>(resource: T, ...addresses: any[]): T {
  return {
    ...resource,
    address: [...(resource.address ?? []), ...addresses],
  };
}

export class FhirArrayBuilders {
  static addIdentifier = addIdentifier;
  static addIdentifiers = addIdentifiers;
  static addTelecom = addTelecom;
  static addAddress = addAddress;
}
